// chart-tools.js
export const getChartColor = (index) => {
  const colors = [
    '#493289', '#f28e2b', '#e15759', '#76b7b2',
    '#59a14f', '#edc948', '#b07aa1', '#ff9da7',
    '#9c755f', '#bab0ac'
  ];
  return colors[index % colors.length];
};

export const renderChart = (chartData) => {
  if (!chartData || !chartData.chartType || !chartData.data) {
    return (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--sa-dark-surface-light)', borderRadius: '4px' }}>
        <pre>{JSON.stringify(chartData, null, 2)}</pre>
      </div>
    );
  }
  switch (chartData.chartType) {
    case 'pie':
      return renderPieChart(chartData);
    case 'bar':
      return renderBarChart(chartData);
    case 'line':
      return renderLineChart(chartData);
    default:
      return (
        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--sa-dark-surface-light)', borderRadius: '4px' }}>
          <pre>{JSON.stringify(chartData, null, 2)}</pre>
        </div>
      );
  }
};
const renderPieChart = (chartData) => {
  const total = chartData.data.reduce((sum, item) => sum + (item.value || 0), 0);

  return (
    <div style={{
      marginTop: '0',
      padding: '0',
    }}>
      <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>{chartData.title || 'Untitled Chart'}</h4>
      {chartData.description && (
        <p style={{ margin: '0 0 10px 0', fontSize: '12px' }}>
          {chartData.description}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Pie chart visualization */}
        <div style={{
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: `conic-gradient(
            ${chartData.data.map((item, i) =>
            `${getChartColor(i)} 0 ${((item.value || 0) / total * 100)}%`
          ).join(', ')}
          )`,
          marginRight: '20px',
          flexShrink: 0
        }}></div>

        {/* Legend */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          {chartData.data.map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: getChartColor(i),
                marginRight: '8px',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '14px' }}>
                {item.label || 'Unlabeled'}: {item.value || 0} ({total > 0 ? Math.round((item.value || 0) / total * 100) : 0}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const renderBarChart = (chartData) => {
  if (!chartData?.data?.length) {
    return (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--sa-dark-surface-light)', borderRadius: '4px' }}>
        <pre>{JSON.stringify(chartData, null, 2)}</pre>
      </div>
    );
  }

  // Extract all values from all series
  const allValues = chartData.data.flatMap(item =>
    item.series.map(series => series.value)
  );
  const maxValue = Math.max(...allValues);
  const minHeight = 20; // Minimum height for very small values

  return (
    <div style={{
      marginTop: '0',
      padding: '0',
    }}>
      <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>{chartData.title || 'Bar Chart'}</h4>
      {chartData.description && (
        <p style={{ margin: '0 0 10px 0', fontSize: '12px' }}>
          {chartData.description}
        </p>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        height: '200px',
        gap: '10px',
        padding: '10px 0',
        borderBottom: '1px solid var(--sa-dark-border)',
        width:' auto',
        maxWidth: 'fit-content',
        justifyContent:' center',
        margin: '60px auto 0'
      }}>
        {chartData.data.map((item, i) => (
          <div key={i} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            gap: '5px'
          }}>
            {/* Render each series as a separate bar */}
            {item.series.map((series, seriesIndex) => {
              const height = maxValue > 0
                ? Math.max(minHeight, (series.value / maxValue) * 180)
                : minHeight;

              return (
                <div key={seriesIndex} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%'
                }}>
                  <div style={{
                    width: '30px',
                    height: `${height}px`,
                    backgroundColor: getChartColor(seriesIndex),
                    borderRadius: '4px 4px 0 0',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: '50%',
                      textAlign: 'center',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff'
                    }}>
                      {series.value}
                    </span>
                  </div>
                </div>
              );
            })}
            <div style={{
              marginTop: '8px',
              fontSize: '8px',
              textAlign: 'center',
              wordBreak: 'break-word',
              width: '100%'
            }}>
              {item.xAxisLabel || `Item ${i + 1}`}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      {chartData.data[0]?.series?.length > 1 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginTop: '15px',
          justifyContent: 'center'
        }}>
          {chartData.data[0].series.map((series, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: getChartColor(i),
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px' }}>{series.seriesName}</span>
            </div>
          ))}
        </div>
      )}

      {/* Y-axis label */}
      {chartData.yAxisLabel && (
        <div style={{
          textAlign: 'center',
          marginTop: '10px',
          fontSize: '12px',
          color: '#666'
        }}>
          {chartData.yAxisLabel}
        </div>
      )}
    </div>
  );
};
const renderLineChart = (chartData) => {
  if (!chartData?.data?.length) {
    return (
      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--sa-dark-surface-light)', borderRadius: '4px' }}>
        <pre>{JSON.stringify(chartData, null, 2)}</pre>
      </div>
    );
  }

  // Extract all values from all series
  const allValues = chartData.data.flatMap(item =>
    item.series.map(series => series.value)
  );
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);
  const valueRange = maxValue - minValue;

  return (
    <div style={{
      marginTop: '0',
      padding: '0',
    }}>
      <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600' }}>{chartData.title || 'Line Chart'}</h4>
      {chartData.description && (
        <p style={{ margin: '0 0 10px 0', fontSize: '12px' }}>
          {chartData.description}
        </p>
      )}

      <div style={{
        height: '200px',
        position: 'relative',
        padding: '20px 0'
      }}>
        {/* Y-axis labels */}
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          bottom: '0',
          width: '30px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          {[maxValue, minValue + (valueRange * 0.5), minValue].map((val, i) => (
            <span key={i} style={{
              fontSize: '10px',
              color: '#666',
              transform: 'translateY(50%)'
            }}>
              {Math.round(val)}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div style={{
          position: 'absolute',
          left: '30px',
          right: '0',
          top: '0',
          bottom: '0',
          padding: '20px 0'
        }}>
          <svg width="100%" height="100%">
            {/* Grid lines */}
            <line
              x1="0" y1="0"
              x2="100%" y2="0"
              stroke="#eee"
              strokeWidth="1"
            />
            <line
              x1="0" y1="50%"
              x2="100%" y2="50%"
              stroke="#eee"
              strokeWidth="1"
            />
            <line
              x1="0" y1="100%"
              x2="100%" y2="100%"
              stroke="#eee"
              strokeWidth="1"
            />

            {/* Render each series as a separate line */}
            {chartData.data[0]?.series?.map((series, seriesIndex) => (
              <g key={seriesIndex}>
                <polyline
                  fill="none"
                  stroke={getChartColor(seriesIndex)}
                  strokeWidth="3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={chartData.data.map((item, i) => {
                    const seriesItem = item.series[seriesIndex];
                    const x = (i / (chartData.data.length - 1)) * 100;
                    const y = valueRange > 0
                      ? 100 - ((seriesItem.value - minValue) / valueRange * 100)
                      : 50;
                    return `${x}%,${y}%`;
                  }).join(' ')}
                />

                {/* Data points */}
                {chartData.data.map((item, i) => {
                  const seriesItem = item.series[seriesIndex];
                  const x = (i / (chartData.data.length - 1)) * 100;
                  const y = valueRange > 0
                    ? 100 - ((seriesItem.value - minValue) / valueRange * 100)
                    : 50;

                  return (
                    <circle
                      key={i}
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="5"
                      fill={getChartColor(seriesIndex)}
                      stroke="#fff"
                      strokeWidth="2"
                    />
                  );
                })}
              </g>
            ))}

            {/* X-axis labels */}
            {chartData.data.map((item, i) => {
              const x = (i / (chartData.data.length - 1)) * 100;

              return (
                <text
                  key={i}
                  x={`${x}%`}
                  y="115%"
                  textAnchor="middle"
                  fontSize="10px"
                  fill="#666"
                >
                  {item.xAxisLabel || `Point ${i + 1}`}
                </text>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Legend */}
      {chartData.data[0]?.series?.length > 1 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginTop: '15px',
          justifyContent: 'center'
        }}>
          {chartData.data[0].series.map((series, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: getChartColor(i),
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px' }}>{series.seriesName}</span>
            </div>
          ))}
        </div>
      )}

      {/* Y-axis label */}
      {chartData.yAxisLabel && (
        <div style={{
          textAlign: 'center',
          marginTop: '10px',
          fontSize: '12px',
          color: '#666'
        }}>
          {chartData.yAxisLabel}
        </div>
      )}
    </div>
  );
};




// Include your full renderPieChart, renderBarChart, and renderLineChart implementations here
// Make sure to also `import { getChartColor }` within this file if needed internally
